// SCR-STAFF-887 — خروجی حسابداری (US-ACC-040 تا 043): تولید دسته خروجی (بازه، شعبه، قالب عمومی CSV یا Excel یا پروفایل نرم‌افزار مقصد، شامل
// اسناد دسته‌های ناموفق)، دانلود، شمار دانلود، علامت «ناموفق در مقصد» با دلیل و ارسال دوباره (AC-ACC-040.2، 042.1)؛ زبانه‌های پروفایل، زمان‌بندی و
// تطبیق با مقصد (AP-ACC-02).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { unwrap, type AccExportBatchView, type AccExportFormat } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  ReasonDialog,
  toBusinessDate,
  type DataTableColumn,
} from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  accKeys,
  BatchStatusChip,
  num,
  saveBlob,
  SCR_STAFF_887,
  useAccBranches,
  useBranchName,
} from './common';
import { ProfilesTab, ReconciliationTab, SchedulesTab } from './ExportTabs';

const TABS = ['batches', 'profiles', 'schedules', 'reconciliation'] as const;
type ExportTab = (typeof TABS)[number];

const FORMATS: readonly AccExportFormat[] = ['Csv', 'Xlsx'];

interface ExportForm {
  from: string | null;
  to: string | null;
}

export function ExportsPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const branches = useAccBranches();
  const branchName = useBranchName();
  const canManage = access.accessMode === 'Full' && access.can('acc.export.manage');
  const [open, setOpen] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [format, setFormat] = useState<AccExportFormat>('Csv');
  const [includeFailed, setIncludeFailed] = useState(true);
  const [failing, setFailing] = useState<AccExportBatchView | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [tab, setTab] = useState<ExportTab>('batches');
  const [profileId, setProfileId] = useState('');
  const profiles = useQuery({
    queryKey: accKeys.profiles(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/export-profiles')),
    enabled: canManage,
  });
  const today = toBusinessDate(new Date());
  const { control, watch } = useForm<ExportForm>({ defaultValues: { from: today, to: today } });
  const from = watch('from');
  const to = watch('to');
  const batches = useInfiniteQuery({
    queryKey: accKeys.exports(session?.tenantId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/export-batches', {
          params: { query: { cursor: pageParam, limit: 50 } },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: accKeys.all(session?.tenantId) });
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/export-batches', {
          body: {
            fromDate: from ?? today,
            toDate: to ?? from ?? today,
            branchId: branchId || null,
            format,
            includeFailed,
            profileId: profileId || null,
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (data) => {
      setOpen(false);
      setCreated(data.number);
      refresh();
    },
  });
  const fail = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/export-batches/{id}:mark-failed', {
          params: { path: { id } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      setFailing(null);
      refresh();
    },
  });
  const retry = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.POST('/api/v1/accounting/export-batches/{id}:retry', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const download = async (batch: AccExportBatchView, kind: AccExportFormat | null) => {
    const response = await tenantApi.GET('/api/v1/accounting/export-batches/{id}/file', {
      params: { path: { id: batch.id }, query: { format: kind ?? undefined } },
      parseAs: 'blob',
    });
    if (response.response.ok && response.data) {
      saveBlob(
        response.data as unknown as Blob,
        `${batch.number}.${(kind ?? batch.format) === 'Csv' ? 'csv' : 'xlsx'}`,
      );
      refresh();
    }
  };
  const columns: DataTableColumn<AccExportBatchView>[] = [
    { id: 'number', headerKey: 'acc:export.number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    {
      id: 'range',
      headerKey: 'acc:export.range',
      cell: (row) => (
        <span>
          <JalaliDate value={row.fromDate} /> – <JalaliDate value={row.toDate} />
        </span>
      ),
    },
    {
      id: 'branch',
      headerKey: 'acc:common.branch',
      cell: (row) => branchName(row.branchId) ?? t('acc:common.all'),
    },
    { id: 'format', headerKey: 'acc:export.format', cell: (row) => t(`acc:export.format_${row.format}`) },
    {
      id: 'profile',
      headerKey: 'acc:export.profile',
      cell: (row) => (
        <span data-testid="acc-batch-profile">
          {row.profileName ?? t('acc:schedule.generic')}
          {row.scheduleId ? <small> · {t('acc:export.scheduled')}</small> : null}
        </span>
      ),
    },
    { id: 'journals', headerKey: 'acc:export.journals', cell: (row) => faDigits(num(row.journalCount)) },
    { id: 'rows', headerKey: 'acc:export.rows', cell: (row) => faDigits(num(row.rowCount)) },
    { id: 'downloads', headerKey: 'acc:export.downloads', cell: (row) => faDigits(num(row.downloadCount)) },
    {
      id: 'status',
      headerKey: 'acc:common.status',
      cell: (row) => (
        <Stack spacing={0.5}>
          <BatchStatusChip status={row.status} />
          {row.failureReason ? <small>{row.failureReason}</small> : null}
        </Stack>
      ),
    },
    {
      id: 'actions',
      headerKey: 'acc:common.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {row.profileId ? (
            <Button
              size="small"
              onClick={() => void download(row, null)}
              data-testid="acc-export-profile-file"
            >
              {t('acc:export.download_profile')}
            </Button>
          ) : (
            <>
              <Button size="small" onClick={() => void download(row, 'Csv')} data-testid="acc-export-csv">
                {t('acc:common.download_csv')}
              </Button>
              <Button size="small" onClick={() => void download(row, 'Xlsx')} data-testid="acc-export-xlsx">
                {t('acc:common.download_excel')}
              </Button>
            </>
          )}
          {canManage && row.status !== 'Failed' && row.status !== 'Confirmed' ? (
            <Button size="small" color="error" onClick={() => setFailing(row)} data-testid="acc-export-fail">
              {t('acc:export.mark_failed')}
            </Button>
          ) : null}
          {canManage && row.status === 'Failed' ? (
            <Button size="small" onClick={() => retry.mutate(row.id)} data-testid="acc-export-retry">
              {t('acc:export.retry')}
            </Button>
          ) : null}
        </Stack>
      ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_887}
      titleKey="acc:export.title"
      subtitleKey="acc:export.subtitle"
      actions={
        canManage && tab === 'batches' ? (
          <Button variant="contained" onClick={() => setOpen(true)} data-testid="acc-export-create">
            {t('acc:export.create')}
          </Button>
        ) : undefined
      }
    >
      {created ? (
        <Alert
          severity="success"
          onClose={() => setCreated(null)}
          sx={{ mb: 2 }}
          data-testid="acc-export-created"
        >
          {t('acc:export.created', { number: created })}
        </Alert>
      ) : null}
      <Tabs
        value={tab}
        onChange={(_event, value: ExportTab) => setTab(value)}
        sx={{ mb: 2 }}
        aria-label={t('acc:export.title')}
      >
        {TABS.map((value) => (
          <Tab
            key={value}
            value={value}
            label={t(`acc:export.tab_${value}`)}
            data-testid={`acc-export-tab-${value}`}
          />
        ))}
      </Tabs>
      {tab === 'profiles' ? <ProfilesTab canManage={canManage} /> : null}
      {tab === 'schedules' ? <SchedulesTab canManage={canManage} /> : null}
      {tab === 'reconciliation' ? <ReconciliationTab /> : null}
      {tab === 'batches' ? (
        <>
          {retry.error ? <ErrorState error={retry.error} compact /> : null}
          <DataTable
            captionKey="acc:export.title"
            columns={columns}
            rows={batches.data?.pages.flatMap((page) => page.items)}
            getRowId={(row) => row.id}
            isLoading={batches.isPending}
            error={batches.error}
            onRetry={() => void batches.refetch()}
            hasMore={batches.hasNextPage}
            onLoadMore={() => void batches.fetchNextPage()}
            isFetchingMore={batches.isFetchingNextPage}
            emptyTitleKey="acc:export.empty"
          />
        </>
      ) : null}
      <FormDialog
        open={open}
        titleKey="acc:export.create"
        onSubmit={() => create.mutate()}
        onClose={() => setOpen(false)}
        isDirty={false}
        isSubmitting={create.isPending}
        submitError={create.error}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <JalaliDatePickerRHF control={control} name="from" labelKey="acc:common.from" />
          <JalaliDatePickerRHF control={control} name="to" labelKey="acc:common.to" />
          <TextField
            select
            label={t('acc:common.branch')}
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
          >
            <MenuItem value="">{t('acc:common.all')}</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.branchId} value={branch.branchId}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('acc:export.format')}
            value={format}
            onChange={(event) => setFormat(event.target.value as AccExportFormat)}
            slotProps={{ htmlInput: { 'data-testid': 'acc-export-format' } }}
          >
            {FORMATS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`acc:export.format_${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('acc:export.profile')}
            value={profileId}
            onChange={(event) => setProfileId(event.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'acc-export-profile' } }}
          >
            <MenuItem value="">{t('acc:schedule.generic')}</MenuItem>
            {(profiles.data?.items ?? [])
              .filter((profile) => profile.isActive)
              .map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.name}
                </MenuItem>
              ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeFailed}
                onChange={(event) => setIncludeFailed(event.target.checked)}
              />
            }
            label={t('acc:export.include_failed')}
          />
        </Stack>
      </FormDialog>
      <ReasonDialog
        open={failing !== null}
        titleKey="acc:export.mark_failed"
        messageKey="acc:export.fail_reason"
        confirmKey="acc:export.mark_failed"
        destructive
        busy={fail.isPending}
        error={fail.error}
        onConfirm={(reason) => failing && fail.mutate({ id: failing.id, reason })}
        onCancel={() => setFailing(null)}
      />
    </ListPage>
  );
}
